import {
  Popover,
  PopoverBackdrop,
  PopoverButton,
  PopoverPanel,
  Transition,
} from '@headlessui/react';
import type { RowData } from '@tanstack/react-table';
import { t } from 'i18next';
import { type ReactNode, type RefObject, useEffect, useId, useRef, useState } from 'react';
import { cn, SHIMMER_CLASSES } from '../../../lib/styles';
import { Button } from '../button/button.spark';
import { Checkbox } from '../checkbox/checkbox.spark';
import { Dropdown } from '../dropdown/dropdown.spark';
import { Heading } from '../heading/heading.spark';
import { Icon } from '../icon/icon.spark';
import { Input } from '../input/input.spark';
import { Text } from '../text/text.spark';
import {
  CellBase,
  CellContent,
  FilterPills,
  InteractiveCell,
  RowActions,
  rowClickHandler,
  TreeCell,
  TreeExpanderSlot,
} from './data-table.shared';
import {
  clampPage,
  clearAllHandler,
  getPageRange,
  getSelectAllAction,
  hasActiveFilters,
  selectionCountLabel,
} from './data-table-utils';
import { useDataTable } from './hooks/use-data-table';
import type {
  DataTableCellProps,
  DataTableColumn,
  DataTableFilter,
  DataTablePaginationModel,
  DataTableProps,
  DataTableRowAction,
  DataTableRowProps,
  DataTableSearch,
  DataTableSelectionModel,
  DataTableSkeletonRowProps,
  DataTableSort,
  DataTableViews,
  SortDirection,
} from './types';
import { DEFAULT_PAGE_SIZE_OPTIONS, DEFAULT_SKELETON_COLS, DEFAULT_SKELETON_ROWS } from './types';

type PickerColumn = Pick<DataTableColumn, 'id' | 'label' | 'alwaysVisible'>;

type ColumnPickerProps = {
  columns: readonly PickerColumn[];
  visibleIds: Set<string>;
  onToggleColumn: (id: string) => void;
};

type ToolbarProps<TSortId extends string = string> = ColumnPickerProps & {
  search?: DataTableSearch;
  sort?: DataTableSort<TSortId>;
  filters: readonly DataTableFilter[];
  columnSelectorEnabled: boolean;
};

type SortPillProps = {
  direction: SortDirection;
  label: string;
  active: boolean;
  onClick: () => void;
};

type PageInputProps = {
  page: number;
  totalPages: number;
  disabled?: boolean;
  setPage: (page: number) => void;
};

type PopoverWrapperProps = {
  trigger: ReactNode;
  panelClassName?: string;
  children: ReactNode;
};

function useHorizontalOverflow(ref: RefObject<HTMLElement | null>) {
  const [overflowing, setOverflowing] = useState<boolean>(false);

  useEffect(() => {
    const scroller = ref.current;
    if (!scroller) return;

    const update = () => setOverflowing(scroller.scrollWidth > scroller.clientWidth);
    update();

    const observer = new ResizeObserver(update);
    observer.observe(scroller);
    for (const child of scroller.children) observer.observe(child);

    return () => observer.disconnect();
  }, [ref]);

  return overflowing;
}

export function DataTable<
  TData extends RowData,
  const TColumns extends readonly DataTableColumn<TData>[] = readonly DataTableColumn<TData>[],
  TSortId extends string = string,
  TViewId extends string = string,
>(props: DataTableProps<TData, TColumns, TSortId, TViewId>) {
  const captionId = useId();

  const table = useDataTable({
    columns: props.columns,
    columnVisibility: props.columnVisibility,
    expansion: props.expansion,
    multiSelect: props.multiSelect,
    pagination: props.pagination,
    data: props.data
      ? { rows: props.data, getRowId: props.getRowId, getSubRows: props.getSubRows }
      : undefined,
  });

  const bulkActions: DataTableRowAction[] = (props.multiSelect?.bulkActions ?? []).map(
    (action) => ({
      ...action,
      onClick: () => action.onClick?.(table.selection?.selectedRows ?? []),
    }),
  );

  const selectAllAction = table.selection && getSelectAllAction(table.selection);

  const hasHeadings = table.columns.visible.length > 0;
  const skeletonCols = hasHeadings
    ? table.columns.visible.length
    : (props.skeleton?.cols ?? DEFAULT_SKELETON_COLS);
  const skeletonRows = props.skeleton?.rows ?? DEFAULT_SKELETON_ROWS;

  const hasToolbar = Boolean(
    props.search ||
      props.sort ||
      props.filters?.length ||
      props.columnVisibility?.columnSelectorEnabled,
  );

  const scrollRef = useRef<HTMLDivElement>(null);
  const overflowing = useHorizontalOverflow(scrollRef);
  const stickyClass = cn(
    'sticky inset-e-0 z-10',
    overflowing && 'shadow-[inset_1px_0_0_var(--color-spark-border-subtle)]',
  );

  return (
    <div className="h-auto w-full overflow-hidden rounded-lg border border-spark-border-subtle bg-spark-surface text-sm text-spark-text-secondary shadow-sm">
      {props.views && <ViewBar views={props.views} />}
      {hasToolbar && (
        <Toolbar
          search={props.search}
          sort={props.sort}
          filters={props.filters ?? []}
          columns={props.columns}
          visibleIds={new Set(table.columns.visible.map((col) => col.id))}
          onToggleColumn={table.columns.toggleVisible}
          columnSelectorEnabled={Boolean(props.columnVisibility?.columnSelectorEnabled)}
        />
      )}
      <div ref={scrollRef} className="w-full overflow-x-auto">
        <table
          className={cn('w-full', props.loading ? 'table-fixed' : 'table-auto')}
          aria-labelledby={captionId}
        >
          <caption id={captionId} className="sr-only">
            {props.caption}
          </caption>
          <thead className="border-spark-border-subtle border-b uppercase tracking-wider">
            <tr>
              {table.selection && (
                <th scope="col" className="w-px bg-spark-surface-subtle px-4 py-2.5 align-middle">
                  {!props.loading && (
                    <Checkbox
                      checked={table.selection.allSelected}
                      indeterminate={table.selection.someSelected}
                      onChange={table.selection.toggleAll}
                    />
                  )}
                </th>
              )}
              {table.selection && table.selection.count > 0 ? (
                <th
                  scope="col"
                  colSpan={Math.max(table.columns.visible.length, 1)}
                  className="bg-spark-surface-subtle px-4 py-2.5 align-middle font-normal normal-case tracking-normal"
                >
                  <div className="flex items-center gap-2">
                    <Text size="md" variant="primary">
                      {selectionCountLabel(table.selection)}
                    </Text>
                    {selectAllAction && (
                      <Button
                        variant="bare"
                        size="xs"
                        className="font-medium text-sm text-spark-primary-accent"
                        onClick={selectAllAction.onClick}
                      >
                        {selectAllAction.label}
                      </Button>
                    )}
                    <RowActions actions={bulkActions} />
                  </div>
                </th>
              ) : hasHeadings ? (
                table.columns.visible.map((col) => (
                  <th
                    scope="col"
                    className={cn(
                      'whitespace-nowrap bg-spark-surface-subtle px-4 py-2.5 text-left align-middle font-medium text-spark-text-primary text-xs',
                      col.sticky && stickyClass,
                    )}
                    key={col.id}
                  >
                    {col.label ?? ''}
                  </th>
                ))
              ) : (
                props.loading &&
                Array.from({ length: skeletonCols }).map((_, i) => (
                  <th
                    scope="col"
                    className="px-4 py-2.5 text-left align-middle"
                    // biome-ignore lint/suspicious/noArrayIndexKey: static skeleton placeholders never reorder
                    key={`skeleton-head-${i}`}
                  >
                    <div className={cn('h-3 w-full rounded', SHIMMER_CLASSES)} />
                  </th>
                ))
              )}
            </tr>
          </thead>
          <tbody className="divide-y divide-spark-border-subtle">
            {props.loading
              ? Array.from({ length: skeletonRows }).map((_, rowIdx) => (
                  <SkeletonRow
                    // biome-ignore lint/suspicious/noArrayIndexKey: static skeleton placeholders never reorder
                    key={`skeleton-row-${rowIdx}`}
                    cols={skeletonCols}
                    rowIndex={rowIdx}
                    hasSelection={!!table.selection}
                  />
                ))
              : (table.rows ?? []).map((row, index) => (
                  <Row
                    key={row.id}
                    row={row}
                    index={index}
                    columns={table.columns.visible}
                    isTree={table.isTree}
                    selection={table.selection}
                    stickyClass={stickyClass}
                    onClick={rowClickHandler(row, props.onRowClick)}
                  />
                ))}
            {!props.loading && (table.rows ?? []).length === 0 && (
              <EmptyResults
                colSpan={table.columns.visible.length + (table.selection ? 1 : 0) || 1}
                onClearAll={
                  hasActiveFilters(props.search, props.filters ?? [])
                    ? clearAllHandler(props.search, props.filters ?? [])
                    : undefined
                }
              />
            )}
          </tbody>
        </table>
      </div>
      {!props.loading && table.pagination && <Pagination {...table.pagination} />}
    </div>
  );
}

function Toolbar<TSortId extends string = string>(props: ToolbarProps<TSortId>) {
  const { search, sort, filters, columnSelectorEnabled, ...picker } = props;

  const showAppliedFiltersBar = filters.some((filter) => filter.value.length > 0);
  const hasActions = filters.length > 0 || Boolean(sort) || columnSelectorEnabled;

  return (
    <>
      <div
        className={cn(
          'flex items-center justify-between gap-2 px-4 py-2',
          // The applied-filters bar supplies the separator when it follows.
          !showAppliedFiltersBar && 'border-spark-border-subtle border-b',
        )}
      >
        <div className="grow">{search && <SearchBar search={search} />}</div>
        {hasActions && (
          <div className="flex items-center gap-2">
            {filters.length > 0 && <FiltersPopover filters={filters} />}
            {sort && <SortPopover sort={sort} />}
            {columnSelectorEnabled && <ColumnsPopover {...picker} />}
          </div>
        )}
      </div>
      {showAppliedFiltersBar && <AppliedFiltersBar filters={filters} />}
    </>
  );
}

function ViewBar<TViewId extends string>({ views }: { views: DataTableViews<TViewId> }) {
  return (
    <div className="flex items-center gap-1 border-spark-border-subtle border-b px-4">
      {views.items.map((view) => {
        const active = view.id === views.value;
        return (
          <button
            type="button"
            key={view.id}
            onClick={() => views.onChange(view.id)}
            className={cn(
              '-mb-px flex cursor-pointer items-center gap-1 border-b-2 px-2.5 py-2.5 text-sm transition hover:bg-spark-surface-hover',
              active
                ? 'border-spark-primary-accent font-medium text-spark-text-primary'
                : 'border-spark-border-subtle text-spark-text-secondary hover:text-spark-text-primary',
            )}
          >
            {view.label}
            {view.count !== undefined && (
              <span
                className={cn(
                  'inline-flex h-4 min-w-4 items-center justify-center rounded-full px-1 font-medium text-[10px] leading-none',
                  active
                    ? 'bg-spark-primary text-spark-text-inverse'
                    : 'bg-spark-surface-muted text-spark-text-secondary',
                )}
              >
                {view.count}
              </span>
            )}
          </button>
        );
      })}
    </div>
  );
}

function SearchBar({ search }: { search: DataTableSearch }) {
  return (
    <Input
      value={search.value}
      onChange={search.onChange}
      placeholder={search.placeholder}
      icon="search"
    />
  );
}

function SortPill({ direction, label, active, onClick }: SortPillProps) {
  return (
    <Button
      size="xs"
      className="rounded-full"
      variant={active ? 'primary' : 'secondary'}
      icon={direction === 'asc' ? 'arrowUp' : 'arrowDown'}
      onClick={onClick}
    >
      {label}
    </Button>
  );
}

function PopoverWrapper({ trigger, panelClassName, children }: PopoverWrapperProps) {
  return (
    <Popover className="relative">
      <PopoverBackdrop className="fixed inset-0 z-20" />
      <PopoverButton className="flex h-spark-component shrink-0 items-center gap-1 rounded-lg border border-spark-border-default bg-spark-surface px-3 text-sm text-spark-text-primary shadow-xs transition hover:bg-spark-surface-hover focus:outline-hidden focus:ring-2 focus:ring-spark-primary-accent focus:ring-offset-2 focus:ring-offset-spark-surface disabled:cursor-not-allowed disabled:opacity-50">
        {trigger}
      </PopoverButton>
      <Transition
        enter="transform transition duration-100 ease-out"
        enterFrom="opacity-0 scale-95"
        enterTo="opacity-100 scale-100"
        leave="transform transition duration-100 ease-in"
        leaveFrom="opacity-100 scale-100"
        leaveTo="opacity-0 scale-95"
      >
        <PopoverPanel
          anchor="bottom end"
          className={cn(
            'z-30 mt-1 min-w-48 rounded-md border border-spark-border-default bg-spark-surface p-3 shadow-lg',
            panelClassName,
          )}
        >
          {children}
        </PopoverPanel>
      </Transition>
    </Popover>
  );
}

function SortPopover<TSortId extends string>({ sort }: { sort: DataTableSort<TSortId> }) {
  if (sort.options.length === 0) return null;

  return (
    <PopoverWrapper
      panelClassName="flex min-w-64 flex-col gap-3"
      trigger={
        <>
          <Icon name="sort" size="sm" />
          {t('tables:sort.label')}
        </>
      }
    >
      <p className="font-semibold text-spark-text-primary text-xs uppercase tracking-wider">
        {t('tables:sort.sort_by')}
      </p>
      <div className="flex flex-col gap-1">
        {sort.options.map((option) => {
          const active = sort.value?.id === option.id ? sort.value.direction : undefined;
          const sortBy = (direction: SortDirection) => sort.onChange({ id: option.id, direction });

          return (
            <div
              key={option.id}
              className="flex items-center justify-between gap-3 rounded-md py-1 ps-3 pe-2 hover:bg-spark-surface-hover"
            >
              <Button
                variant="bare"
                size="sm"
                className={cn(active && 'font-semibold')}
                onClick={() => sortBy(active === 'asc' ? 'desc' : 'asc')}
              >
                {option.label}
              </Button>
              <div className="flex items-center gap-1">
                <SortPill
                  direction="asc"
                  label={t('tables:sort.a_z')}
                  active={active === 'asc'}
                  onClick={() => sortBy('asc')}
                />
                <SortPill
                  direction="desc"
                  label={t('tables:sort.z_a')}
                  active={active === 'desc'}
                  onClick={() => sortBy('desc')}
                />
              </div>
            </div>
          );
        })}
      </div>
      <div className="border-spark-border-subtle border-t pt-2">
        <Button
          variant="bare"
          size="sm"
          disabled={!sort.value}
          onClick={() => sort.onChange(undefined)}
        >
          {t('tables:sort.clear')}
        </Button>
      </div>
    </PopoverWrapper>
  );
}

function FiltersPopover({ filters }: { filters: readonly DataTableFilter[] }) {
  const appliedFiltersCount = filters.filter((filter) => filter.value.length > 0).length;
  const clearFilters = () => filters.map((filter) => filter.onChange([]));

  return (
    <PopoverWrapper
      panelClassName="flex min-w-64 max-w-sm! flex-col gap-3"
      trigger={
        <>
          <Icon name="filter" size="sm" />
          {t('tables:filters.label')}
          {appliedFiltersCount > 0 && (
            <span className="inline-flex h-4 min-w-4 items-center justify-center rounded-full bg-spark-primary px-1 font-medium text-[10px] text-spark-text-inverse leading-none">
              {appliedFiltersCount}
            </span>
          )}
        </>
      }
    >
      {filters.map((filter) => (
        <div key={filter.id} className="flex flex-col gap-2">
          <p className="font-semibold text-spark-text-primary text-xs uppercase tracking-wider">
            {filter.label}
          </p>
          <div className="ps-1">
            <FilterPills
              options={filter.options}
              selected={filter.value}
              setSelected={filter.onChange}
              multiple={filter.multiple}
              activeClassName="border-spark-primary-accent bg-spark-primary-soft text-spark-primary-accent"
            />
          </div>
        </div>
      ))}
      <div className="border-spark-border-subtle border-t pt-2">
        <Button
          variant="secondary"
          size="sm"
          disabled={appliedFiltersCount === 0}
          onClick={clearFilters}
        >
          {t('tables:filters.clear')}
        </Button>
      </div>
    </PopoverWrapper>
  );
}

function AppliedFiltersBar({ filters }: { filters: readonly DataTableFilter[] }) {
  const applied = filters.filter((filter) => filter.value.length > 0);
  if (applied.length === 0) return null;

  return (
    <div className="flex flex-wrap items-center gap-2 border-spark-border-subtle border-b px-4 pt-0 pb-2">
      {applied.map((filter) => (
        <span
          key={filter.id}
          className="inline-flex items-center gap-1 rounded-full border border-spark-primary-accent bg-spark-primary-soft py-0.5 ps-2 pe-1 text-spark-primary-accent text-xs"
        >
          {`${filter.label}: ${filter.value
            .map((value) => filter.options.find((option) => option.id === value)?.label ?? value)
            .join(', ')}`}
          <Button
            variant="bare"
            size="xs"
            icon="x"
            ariaLabel={t('buttons:clear')}
            className="size-4 text-spark-primary-accent/70 hover:text-spark-primary-accent"
            onClick={() => filter.onChange([])}
          />
        </span>
      ))}
      <Button
        variant="bare"
        size="xs"
        className="ms-1"
        onClick={() => applied.map((filter) => filter.onChange([]))}
      >
        {t('tables:filters.clear_all')}
      </Button>
    </div>
  );
}

function ColumnsPopover(props: ColumnPickerProps) {
  return (
    <PopoverWrapper
      trigger={
        <>
          <Icon name="viewColumns" size="sm" />
          {t('buttons:columns')}
        </>
      }
    >
      <Checklist {...props} />
    </PopoverWrapper>
  );
}

function Pagination(props: DataTablePaginationModel) {
  const {
    page,
    totalPages,
    setPage,
    pageSize,
    setPageSize,
    total,
    pageSizeOptions = DEFAULT_PAGE_SIZE_OPTIONS,
    disabled,
  } = props;

  const range = getPageRange(page, pageSize, total);

  return (
    <div className="flex flex-wrap items-center justify-between gap-3 border-spark-border-subtle border-t px-4 py-2">
      {/* Rows-per-page selector + the current range */}
      <div className="flex items-center gap-3">
        <div className="flex items-center gap-2">
          <Text size="md" variant="secondary">
            {t('tables:pagination.rows_per_page')}
          </Text>
          <div className="w-20">
            <Dropdown
              value={String(pageSize)}
              disabled={disabled}
              options={pageSizeOptions.map((size) => ({
                value: String(size),
                label: String(size),
              }))}
              onChange={(value) => setPageSize(Number(value))}
            />
          </div>
        </div>
        {range && (
          <Text size="md" variant="secondary">
            {total !== undefined
              ? t('tables:pagination.range_with_total', {
                  first: range.first,
                  last: range.last,
                  total,
                })
              : t('tables:pagination.range', { first: range.first, last: range.last })}
          </Text>
        )}
      </div>

      <div className="flex items-center gap-1">
        <Button
          variant="secondary"
          icon="chevronsLeft"
          ariaLabel={t('aria:first_page')}
          disabled={!!disabled || page <= 1}
          onClick={() => setPage(1)}
        />
        <Button
          variant="secondary"
          icon="back"
          ariaLabel={t('aria:previous_page')}
          disabled={!!disabled || page <= 1}
          onClick={() => setPage(page - 1)}
        />
        {page > 1 && (
          <Button
            variant="secondary"
            className="min-w-spark-component"
            disabled={disabled}
            onClick={() => setPage(page - 1)}
          >
            {page - 1}
          </Button>
        )}
        <PageInput page={page} totalPages={totalPages} disabled={disabled} setPage={setPage} />
        {page < totalPages && (
          <Button
            variant="secondary"
            className="min-w-spark-component"
            disabled={disabled}
            onClick={() => setPage(page + 1)}
          >
            {page + 1}
          </Button>
        )}
        <Button
          variant="secondary"
          icon="chevronRight"
          ariaLabel={t('aria:next_page')}
          disabled={!!disabled || page >= totalPages}
          onClick={() => setPage(page + 1)}
        />
        <Button
          variant="secondary"
          icon="chevronsRight"
          ariaLabel={t('aria:last_page')}
          disabled={!!disabled || page >= totalPages}
          onClick={() => setPage(totalPages)}
        />
      </div>
    </div>
  );
}

function PageInput({ page, totalPages, disabled, setPage }: PageInputProps) {
  const [value, setValue] = useState<string>(String(page));

  useEffect(() => {
    setValue(String(page));
  }, [page]);

  const commit = () => {
    const next = clampPage(value, totalPages);
    setValue(String(next));
    if (next !== page) {
      setPage(next);
    }
  };

  return (
    <input
      type="text"
      inputMode="numeric"
      aria-label={t('aria:go_to_page')}
      value={value}
      disabled={disabled}
      onChange={(e) => setValue(e.target.value)}
      onBlur={commit}
      onKeyDown={(e) => {
        if (e.key === 'Enter') {
          e.currentTarget.blur();
        }
      }}
      className="h-spark-component w-spark-component rounded-lg border border-spark-border-default bg-spark-surface px-2 text-center text-sm text-spark-text-primary shadow-xs transition focus:border-spark-primary-accent focus:outline-hidden focus:ring-1 focus:ring-spark-primary-accent disabled:cursor-not-allowed disabled:bg-spark-surface-muted disabled:text-spark-text-secondary"
    />
  );
}

function SkeletonRow({
  cols,
  rowIndex,
  hasSelection,
}: DataTableSkeletonRowProps & { hasSelection: boolean }) {
  return (
    <tr aria-hidden>
      {hasSelection && <td className="w-px px-4 py-3 align-middle" />}
      {Array.from({ length: cols }).map((_, colIdx) => (
        // biome-ignore lint/suspicious/noArrayIndexKey: static skeleton placeholders never reorder
        <td key={`skeleton-${rowIndex}-${colIdx}`} className="px-4 py-3 align-middle">
          <div className={cn('h-4 w-full rounded-full', SHIMMER_CLASSES)} />
        </td>
      ))}
    </tr>
  );
}

// TODO: use the shared EmptyState component instead of this hand-rolled markup.
function EmptyResults({ colSpan, onClearAll }: { colSpan: number; onClearAll?: () => void }) {
  return (
    <tr>
      <td colSpan={colSpan} className="px-4 py-12">
        <div className="mx-auto flex max-w-sm flex-col items-center gap-2 text-center">
          <span className="flex size-12 items-center justify-center rounded-full bg-spark-surface-muted text-spark-text-secondary">
            <Icon name="search" size="md" />
          </span>
          <Heading level="h2" style="sm">
            {t('tables:empty.title')}
          </Heading>
          <Text variant="secondary">{t('tables:empty.description')}</Text>
          {onClearAll && (
            <div className="mt-1">
              <Button variant="secondary" size="sm" onClick={onClearAll}>
                {t('tables:filters.clear_all')}
              </Button>
            </div>
          )}
        </div>
      </td>
    </tr>
  );
}

function Row<TData>({
  row,
  index,
  columns,
  isTree,
  selection,
  stickyClass,
  onClick,
}: Omit<DataTableRowProps<TData>, 'selected'> & {
  selection?: DataTableSelectionModel<TData>;
  stickyClass: string;
}) {
  const selected = selection?.isSelected(row.id) ?? false;
  const background = selected
    ? 'bg-spark-surface-muted'
    : row.depth > 0
      ? 'bg-spark-surface-subtle'
      : 'bg-spark-surface';
  const hoverBackground = selected
    ? 'hover:bg-spark-surface-hover-strong'
    : row.depth > 0
      ? 'hover:bg-spark-surface-hover'
      : 'hover:bg-spark-surface-subtle';

  return (
    <tr
      className={cn(
        'whitespace-nowrap transition',
        background,
        onClick && ['cursor-pointer', hoverBackground],
      )}
      id={row.id}
      onClick={onClick}
    >
      {selection && (
        <td className="w-px px-4 py-2 align-middle">
          <InteractiveCell>
            <Checkbox
              checked={selection.isSelected(row.id)}
              indeterminate={selection.isIndeterminate(row.id)}
              onChange={() => selection.toggle(row.id)}
            />
          </InteractiveCell>
        </td>
      )}
      {columns.map((col, colIndex) => {
        const capped = <CellContent column={col}>{col.renderCell(row.data, index)}</CellContent>;
        const content =
          isTree && colIndex === 0 ? (
            <TreeCell
              row={row}
              chevron={<Icon name="chevronDown" size="sm" />}
              connector={<Icon name="inherit" size="sm" />}
              expanderClassName="text-spark-text-primary"
              connectorClassName="text-spark-text-tertiary"
            >
              {capped}
            </TreeCell>
          ) : (
            capped
          );

        return (
          <td
            key={col.id}
            className={cn('px-4 py-2 align-middle', col.sticky && [stickyClass, 'bg-inherit'])}
          >
            {col.interactive ? <InteractiveCell>{content}</InteractiveCell> : content}
          </td>
        );
      })}
    </tr>
  );
}

function Cell(props: DataTableCellProps) {
  return <CellBase {...props} gap="gap-2" />;
}

function Checklist({ columns, visibleIds, onToggleColumn }: ColumnPickerProps) {
  return (
    <fieldset className="flex flex-col gap-1">
      <legend className="mb-2 font-semibold text-spark-text-primary text-xs uppercase tracking-wider">
        {t('tables:columns.title')}
      </legend>
      {columns.map((col) => {
        if (col.alwaysVisible) {
          if (!col.label) return null;
          return (
            <div key={col.id} className="rounded-md py-1 ps-3 pe-2 opacity-60 [&_p]:text-[11px]">
              <Checkbox
                label={col.label}
                description={t('tables:columns.always_visible')}
                checked
                disabled
              />
            </div>
          );
        }
        return (
          <div key={col.id} className="rounded-md py-1 ps-3 pe-2 hover:bg-spark-surface-hover">
            <Checkbox
              label={col.label ?? col.id}
              checked={visibleIds.has(col.id)}
              onChange={() => onToggleColumn(col.id)}
            />
          </div>
        );
      })}
    </fieldset>
  );
}

DataTable.Cell = Cell;
DataTable.Actions = RowActions;
DataTable.Expander = TreeExpanderSlot;
